import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { projectNav } from '@/components/px/nav'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import { selectFitGapSummary } from '@/features/requirements/slices/requirementsSlice'
import {
  gatesErrorDismissed,
  gatesRefetchArmed,
  openGates,
  selectGates,
  selectGatesError,
  selectGatesLoading,
  selectPhaseView
} from '@/features/phase-gates/slices/phaseGatesSlice'
import { closeProject, openProject } from '@/features/projects/slices/projectDetailSlice'
import { GenerateTasksPanel } from '@/features/requirements/components/GenerateTasksPanel'
import { RequirementsRegisterPanel } from '@/features/requirements/components/RequirementsRegisterPanel'
import { asApiError } from '@/services/apiError'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * 3.3 Discover — the requirements register (PX-W7.2, TASK-60028F48), now
 * wired onto `features/requirements` (PX-W3.6). `RequirementsRegisterPanel`
 * owns the fetch, the filters, the three data states and the blocked
 * surface; this page composes it with the Discovery gate card.
 *
 * Deferred findings c1-4/c1-7/c1-6 (TASK-71426B1C): the card used to render
 * only when `phase !== null` and nothing on this route ever populated
 * `features/phase-gates`, so it was silently absent on a cold load AND after
 * an in-app rail navigation. This page now dispatches the shared, GUARDED
 * `openGates` itself (its own `shouldFetch` condition is the dedupe against
 * a same-page re-dispatch; a hop from the overview DOES read the gates again,
 * since the overview's unmount runs `gatesClosed()`, which invalidates them —
 * corrected in TASK-41CE1DE2 fix cycle 1) and always renders the section: loading, the
 * gate's state, or an explicit "not set yet" state — never a silent gap. The
 * gate is matched by `ref === 'flow.01'` ONLY (`ref` is the documented
 * identifier; the prior `step === 'STEP 01'` disjunct was a fixture-shaped
 * fallback that could match a demo gate with a different `ref`). This page
 * does not own the phase-gates slice — no reset effect, no cleanup (memory
 * `px-hybrid-route-chrome-leaks-the-fixture`).
 *
 * Fix cycle 1 (GRP-82297E9A, deferred finding): a FAILED gates read used to
 * fall through to the same "not set yet" line as a genuinely empty read —
 * indistinguishable to the consultant. `gatesError` now branches before the
 * not-set check, mirroring `BlueprintPage`'s `gateListError` precedent: a
 * 403/404 (no access to this project's gates) renders the blocked line, any
 * other status renders the shared `ErrorPanel` with a retry that re-arms the
 * slice's fetch cache (`gatesRefetchArmed`, exported by the slice) before
 * re-dispatching `openGates` — a bare retry would otherwise be a silent
 * no-op against an un-invalidated cache key (the same class of bug fixed on
 * `RequirementsRegisterPanel`'s own retry, c1-2).
 *
 * Finding F4 (TASK-A274D065): a refused kickoff on the overview
 * (`startEngagement.rejected`) writes an unscoped error into this same
 * `phase-gates` slice, sharing the channel with a genuine gates-read
 * failure. Since `features/phase-gates/**` is fenced (C-10) and cannot tag
 * that error at the source, this page reads the unscoped channel as a READ
 * failure only when NOTHING is loaded yet (`nothingLoaded`) — a failed
 * FIRST load always leaves nothing loaded (a different project's `pending`
 * clears `gates`/`phase` first), so that case is unaffected. Once gates are
 * loaded, a refused kickoff leaves the card showing the last-good gate
 * state instead of masking it with `ErrorPanel` or the blocked line.
 */
export default function RequirementsRegisterPage() {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const { projectId = '' } = useParams()
  const orgId = useAppSelector(selectActiveOrgId) ?? ''
  const summary = useAppSelector(selectFitGapSummary)
  const phase = useAppSelector(selectPhaseView)
  const gates = useAppSelector(selectGates)
  const gatesLoading = useAppSelector(selectGatesLoading)
  const gatesError = useAppSelector(selectGatesError)

  const nav = projectNav({ id: projectId, client: projectId })
  const discoveryGate = gates.find((gate) => gate.ref === 'flow.01') ?? null
  // F4 (TASK-A274D065): nothing loaded yet == no gates and no phase — the
  // only state a genuine failed FIRST read and a refused kickoff can't be
  // told apart in, so it's the only state the unscoped channel is read as
  // this card's own read failure.
  const nothingLoaded = gates.length === 0 && phase === null
  // A row/verb refusal (e.g. a decision on another gate) carries `gateRef`;
  // only the unscoped primary-read failure belongs to this card, and only
  // while nothing is loaded (F4 — see the JSDoc paragraph above).
  const gatesReadError = nothingLoaded && gatesError !== null && gatesError.gateRef === undefined ? gatesError : null
  const gatesBlocked =
    gatesReadError !== null && (gatesReadError.status === 403 || gatesReadError.status === 404)
      ? gatesReadError
      : null
  const gatesReadFailure = gatesReadError !== null && gatesBlocked === null ? gatesReadError : null

  useEffect(() => {
    if (orgId !== '' && projectId !== '') {
      void dispatch(openGates({ orgId, projectId }))
    }
  }, [dispatch, orgId, projectId])

  // CR-BUILD-BOARD-EPICS-2026-09-26: the Generate action's gate (INV-R.7) reads
  // the project detail's `permissions.write` and roster — mounted exactly as
  // BuildBoardPage does. `openProject` is guarded by `shouldFetch`, but a hop
  // from the overview DOES re-read the project once: the overview's unmount
  // cleanup runs `closeProject()`, which invalidates the cache before this
  // page mounts (fix cycle 1 corrected the earlier "no second request" note).
  useEffect(() => {
    if (orgId === '' || projectId === '') return
    void dispatch(openProject({ orgId, projectId }))
    return () => {
      dispatch(closeProject())
    }
  }, [dispatch, orgId, projectId])

  const onGatesRetryClicked = (): void => {
    dispatch(gatesErrorDismissed())
    dispatch(gatesRefetchArmed())
    if (orgId !== '' && projectId !== '') {
      void dispatch(openGates({ orgId, projectId }))
    }
  }

  return (
    <AppShell
      moduleLabel={t('requirements.moduleLabel')}
      breadcrumb={t('requirements.title')}
      contextPill={t('requirements.contextPill', { count: summary.total })}
      nav={nav}
    >
      <header className="max-w-4xl">
        <h1 className="font-serif text-2xl font-semibold text-foreground">{t('requirements.title')}</h1>
        <p className="mt-1.5 text-sm text-ink-soft">{t('requirements.standfirst')}</p>
      </header>

      {orgId !== '' && projectId !== '' && (
        <GenerateTasksPanel orgId={orgId} projectId={projectId} className="mt-5" />
      )}

      <div className="mt-5">
        <RequirementsRegisterPanel orgId={orgId} projectId={projectId} />
      </div>

      <section
        aria-labelledby="discovery-gate-heading"
        data-testid="discovery-gate-card"
        className="mt-6 rounded-lg border border-border bg-card px-4 py-4"
      >
        <SectionLabel>{t('requirements.gate.label')}</SectionLabel>
        <h2 id="discovery-gate-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
          {t('requirements.gate.heading')}
        </h2>
        {gatesLoading && nothingLoaded ? (
          <div className="mt-2.5">
            <LoadingPanel label={t('requirements.gate.loading')} />
          </div>
        ) : gatesBlocked !== null ? (
          <p className="mt-1.5 text-sm text-ink-soft" data-testid="discovery-gate-blocked">
            {t('requirements.gate.blocked', { status: gatesBlocked.status })}
          </p>
        ) : gatesReadFailure !== null ? (
          <div className="mt-2.5">
            <ErrorPanel
              error={asApiError({
                status: gatesReadFailure.status,
                key: gatesReadFailure.code,
                message: gatesReadFailure.message
              })}
              onRetryClicked={onGatesRetryClicked}
            />
          </div>
        ) : discoveryGate === null || phase === null ? (
          <p className="mt-1.5 text-sm text-ink-soft" data-testid="discovery-gate-not-set">
            {t('requirements.gate.notSet')}
          </p>
        ) : (
          <div data-testid="discovery-gate-state">
            <p className="mt-1.5 text-sm text-foreground">
              {t('requirements.gate.phaseLine', { phase: phase.currentPhase ?? t('requirements.gate.noPhase') })}
            </p>
            <div className="mt-2.5">
              <StatusPill tone={discoveryGate.open ? 'review' : 'done'}>
                {discoveryGate.open ? t('requirements.gate.open') : t('requirements.gate.closed')}
              </StatusPill>
            </div>
          </div>
        )}
      </section>
    </AppShell>
  )
}
